import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProductionBatch, Scan } from '../../../model';
import { euclideanDeltaC } from '../../../utils/color-tools';

interface MetricStats {
  count: number;          // scans contributing a usable value
  total: number;          // total scans on batch
  avg: number | null;
  max: number | null;
  min: number | null;
  // Sample stddev (n-1 denominator). null when count < 2.
  stddev: number | null;
  // Scans whose value equals max / min — multiple if tied. Used to render
  // comma-separated clickable sequence numbers.
  maxScans: Scan[];
  minScans: Scan[];
}

const EMPTY: MetricStats = {
  count: 0,
  total: 0,
  avg: null,
  max: null,
  min: null,
  stddev: null,
  maxScans: [],
  minScans: [],
};

@Component({
  selector: 'app-batch-summary-stats',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './batch-summary-stats.component.html',
  styleUrl: './batch-summary-stats.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchSummaryStatsComponent {
  batch = input.required<ProductionBatch>();
  scanSelected = output<Scan>();

  // Pre-resolve every scan's (scan, dE, dC) tuple once. dC is computed via
  // the chroma-plane Euclidean distance against the batch reference Lab —
  // identical to the formula used elsewhere in the UI.
  private readonly resolved = computed(() => {
    const b = this.batch();
    const ref = b?.color?.lab;
    const scans = b?.scans ?? [];
    return scans.map(s => ({
      scan: s,
      dE: typeof s.dE === 'number' ? s.dE : null,
      dC: euclideanDeltaC(ref, s.predicted) ?? null,
    }));
  });

  totalScans = computed(() => this.resolved().length);

  // Scans that contribute to ANY metric — i.e. have at least one of
  // (dE, dC) usable. Header line `number of scans with values: ZZ`.
  scansWithAnyValue = computed(() =>
    this.resolved().filter(r => r.dE != null || r.dC != null).length
  );

  dEStats = computed<MetricStats>(() => this.computeStats(r => r.dE));
  dCStats = computed<MetricStats>(() => this.computeStats(r => r.dC));

  private computeStats(pick: (r: { scan: Scan; dE: number | null; dC: number | null }) => number | null): MetricStats {
    const all = this.resolved();
    const total = all.length;
    if (total === 0) return EMPTY;

    const usable = all.filter(r => pick(r) != null) as Array<{ scan: Scan; dE: number | null; dC: number | null }>;
    const count = usable.length;
    if (count === 0) return { ...EMPTY, total };

    const values = usable.map(r => pick(r) as number);
    const sum = values.reduce((acc, v) => acc + v, 0);
    const avg = sum / count;
    const max = Math.max(...values);
    const min = Math.min(...values);
    // Sample stddev needs at least 2 data points to be defined.
    let stddev: number | null = null;
    if (count >= 2) {
      const variance = values.reduce((acc, v) => acc + (v - avg) ** 2, 0) / (count - 1);
      stddev = Math.sqrt(variance);
    }
    // Match by exact equality — works because we just pulled the same
    // numbers out. Order preserved by sequenceInBatch since `usable` keeps
    // batch.scans order; we sort defensively to keep links stable.
    const maxScans = usable
      .filter(r => pick(r) === max)
      .map(r => r.scan)
      .sort((a, b) => a.sequenceInBatch - b.sequenceInBatch);
    const minScans = usable
      .filter(r => pick(r) === min)
      .map(r => r.scan)
      .sort((a, b) => a.sequenceInBatch - b.sequenceInBatch);

    return { count, total, avg, max, min, stddev, maxScans, minScans };
  }

  onScanLinkClick(scan: Scan, ev: MouseEvent): void {
    ev.preventDefault();
    this.scanSelected.emit(scan);
  }

  // Template helper — formats `XX.YY` or '—' for null.
  format(v: number | null): string {
    return v == null ? '—' : v.toFixed(2);
  }
}
